import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, Alert, LinearProgress,
  Stack, Chip, Divider,
} from '@mui/material';
import {
  Groups as GroupsIcon,
  PlayArrow as StartIcon,
  Refresh as ReplanIcon,
  WbSunny as TomorrowIcon,
  Restaurant as RestaurantIcon,
} from '@mui/icons-material';
import { executiveAssignmentsApi, rhythmApi, authApi } from '../api/services';
import type { ExecutiveAssignment, StartMyDayResponse, RestaurantResult } from '../types/models';
import RestaurantSearchDialog from '../components/RestaurantSearchDialog';

interface UserInfo {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
}

interface ExecutiveState {
  assignment: ExecutiveAssignment;
  user: UserInfo | null;
  dayData: StartMyDayResponse | null;
  loading: boolean;
  error: string | null;
}

export default function EAWorkspacePage() {
  const [executives, setExecutives] = useState<ExecutiveState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [restaurantDialogOpen, setRestaurantDialogOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<RestaurantResult | null>(null);

  const handleRestaurantSelect = (restaurant: RestaurantResult) => {
    setSelectedRestaurant(restaurant);
    // Navigate to calendar page with restaurant info as a simple alert for now
    window.location.href = `/calendar`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [assignments, usersData] = await Promise.all([
          executiveAssignmentsApi.getAll(),
          authApi.getUsers(),
        ]);
        setUsers(usersData);
        const activeAssignments = assignments.filter(a => a.isActive);
        setExecutives(activeAssignments.map(a => ({
          assignment: a,
          user: usersData.find(u => u.id === a.executiveUserId) || null,
          dayData: null,
          loading: false,
          error: null,
        })));
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load assignments');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleAction = async (index: number, action: 'start' | 'replan' | 'planTomorrow') => {
    setExecutives(prev => prev.map((e, i) => i === index ? { ...e, loading: true, error: null } : e));
    try {
      const execUserId = executives[index].assignment.executiveUserId;
      let data: StartMyDayResponse;
      if (action === 'start') {
        data = await rhythmApi.startMyDay({ forExecutiveUserId: execUserId } as any);
      } else if (action === 'replan') {
        data = await rhythmApi.replan({ forExecutiveUserId: execUserId } as any);
      } else {
        data = await rhythmApi.planTomorrow({ forExecutiveUserId: execUserId } as any);
      }
      setExecutives(prev => prev.map((e, i) => i === index ? { ...e, dayData: data, loading: false } : e));
    } catch (err: any) {
      setExecutives(prev => prev.map((e, i) => i === index
        ? { ...e, loading: false, error: err.response?.data?.message || err.message || 'Action failed' }
        : e
      ));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <GroupsIcon sx={{ color: 'primary.main' }} />
        <Box>
          <Typography variant="h4">EA Workspace</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your assigned executives
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && executives.length === 0 && (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <GroupsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>No executive assignments found</Typography>
          <Typography color="text.secondary">
            Contact your admin to get assigned to an executive.
          </Typography>
        </Card>
      )}

      <Grid container spacing={2.5}>
        {executives.map((exec, index) => (
          <Grid size={{ xs: 12 }} key={exec.assignment.id}>
            <Card>
              <CardContent>
                {/* Executive Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h6">
                      {exec.user?.displayName || exec.assignment.executiveUserId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {exec.user?.email || 'Executive'}
                      {exec.user && (
                        <Chip label={exec.user.role} size="small" variant="outlined" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                  </Box>
                  <Chip
                    label={exec.assignment.isActive ? 'Active' : 'Inactive'}
                    color={exec.assignment.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Quick Actions */}
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Quick Actions</Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<StartIcon />}
                    onClick={() => handleAction(index, 'start')}
                    disabled={exec.loading}
                  >
                    Start Their Day
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ReplanIcon />}
                    onClick={() => handleAction(index, 'replan')}
                    disabled={exec.loading}
                  >
                    Replan
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TomorrowIcon />}
                    onClick={() => handleAction(index, 'planTomorrow')}
                    disabled={exec.loading}
                  >
                    Plan Tomorrow
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RestaurantIcon />}
                    onClick={() => setRestaurantDialogOpen(true)}
                  >
                    Book Restaurant
                  </Button>
                </Stack>

                {exec.loading && <LinearProgress sx={{ mb: 2 }} />}
                {exec.error && <Alert severity="error" sx={{ mb: 2 }}>{exec.error}</Alert>}

                {/* Today's Overview */}
                {exec.dayData && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Today's Overview</Typography>
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                          <Typography variant="caption" color="text.secondary">Available</Typography>
                          <Typography variant="h6">
                            {Math.round(exec.dayData.capacity.availableMinutes / 60 * 10) / 10}h
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                          <Typography variant="caption" color="text.secondary">Meetings</Typography>
                          <Typography variant="h6">
                            {Math.round(exec.dayData.capacity.meetingMinutes / 60 * 10) / 10}h
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                          <Typography variant="caption" color="text.secondary">Planned</Typography>
                          <Typography variant="h6">
                            {Math.round(exec.dayData.capacity.plannedMinutes / 60 * 10) / 10}h
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                          <Typography variant="caption" color="text.secondary">Utilisation</Typography>
                          <Typography variant="h6">
                            {Math.round(exec.dayData.capacity.utilisationPercent)}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 1.5 }}>
                      <Chip
                        label={`Plan: ${exec.dayData.plan.status}`}
                        size="small"
                        color={exec.dayData.plan.status === 'Active' ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                      {exec.dayData.plan.isOverloaded && (
                        <Chip label="Overloaded" size="small" color="error" variant="outlined" />
                      )}
                      {exec.dayData.warnings.length > 0 && (
                        <Chip label={`${exec.dayData.warnings.length} warnings`} size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />
                      )}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Restaurant Search Dialog */}
      <RestaurantSearchDialog
        open={restaurantDialogOpen}
        onClose={() => setRestaurantDialogOpen(false)}
        onSelect={handleRestaurantSelect}
      />
    </Box>
  );
}
